import { useState } from 'react'

const STORAGE_KEY = 'fe_post_checkout_welcome'

/**
 * True when the visitor has just returned from Stripe Checkout to
 * /get-the-app (the existing success redirect). Read-only detection — it does
 * not call the backend or change the redirect.
 *
 * Signals: a Stripe Checkout referrer, or a checkout-style query parameter
 * (session_id / checkout=success). Once seen, it's remembered for the tab so
 * a refresh keeps the welcome.
 *
 * `preview` lets the design canvas render the post-checkout state.
 */
export function usePostCheckoutArrival(preview?: boolean): boolean {
  const [arrived] = useState(() => {
    if (preview) return true
    if (typeof window === 'undefined') return false

    try {
      if (window.sessionStorage.getItem(STORAGE_KEY) === '1') return true
    } catch {
      // sessionStorage unavailable — fall through to live signals
    }

    const params = new URLSearchParams(window.location.search)
    const fromStripe = /^https:\/\/checkout\.stripe\.com/.test(document.referrer)
    const checkoutParam =
      params.has('session_id') || ['success', 'complete', 'completed'].includes(params.get('checkout') ?? '')

    const detected = fromStripe || checkoutParam
    if (detected) {
      try {
        window.sessionStorage.setItem(STORAGE_KEY, '1')
      } catch {
        // ignore
      }
    }
    return detected
  })

  return arrived
}
